import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit'
import { ColorTypes } from 'styled-components'

export interface Children {
  email: string
  full_name: string
  first_name: string
  id: string
  is_waiver_read: boolean | null
  is_waiver_signed: boolean | null
  has_appointment: boolean
  member_id: string
  name: string
  image_link?: string
  photo_url: string | null
  waiver_id: string | null
}

export interface AuthState {
  user: User | null
  loginLoading: boolean
  sessionLoading: boolean
  sessionRequested: boolean
  registrationDialog: boolean
  registrationBanner: boolean
  csrf: string
}

export interface User {
  verified: boolean
  last_login_date: string
  address: Address
  role_id: string
  role_name: string
  role_display_name: string
  role_level: number
  role_scheme: Rolescheme
  permissions: Permission[]
  permission_ids: string[]
  id: string
  created_date: string
  updated_date: string
  name: string
  first_name: string
  last_name: string
  email: string
  phone_number: string
  preferred_contact: string
  school_name: string
  gender?: any
  dob?: any
  photo_url?: any
  status?: any
  user_waiver?: any
  waiver_id?: any
  waiver_url?: any
  is_show_registration_dialog?: boolean
  is_enrollment_allowed?: boolean
  is_waiver_signed?: any
  is_waiver_read?: any
  organization_id?: string
  children: Children[] | null
  children_ids: string[]
  reset_token: string
  parent?: User
  pronoun?: string
  specific_pronoun?: string
  pick_up_permission?: string
  has_allergies?: string
  has_epi_pen?: string
  has_medications?: string
  heard_of?: string
  reason_for_joining?: string
  grade?: string
  is_reset_password?: boolean
  participant_email?: string
}

export interface Address {
  city: string
  unit: string
  state: string
  address: string
  postal_code: string
  street_address: string
}

export interface Permission {
  id: string
  status: string
  created_date: string
  updated_date: string
  name: string
}

export interface Rolescheme {
  main: ColorTypes
  black: string
  label: string
  white: string
  border: string
  darkMain: string
  textLink: string
  lightGrey: string
  secondary: string
  focusShadow: string
  inputBorder: string
  tableAlternate: string
}

export interface LoginPayload {
  username: string
  password: string
  remember_me: boolean
}

const initialState: AuthState = {
  user: null,
  sessionLoading: false,
  sessionRequested: false,
  loginLoading: false,
  registrationDialog: false,
  registrationBanner: true,
  csrf: '',
}

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authorize(state, action: PayloadAction<User>) {
      state.user = null
      state.user = action.payload
      state.sessionLoading = false
    },
    unauthorize(state) {
      state.user = null
      state.sessionLoading = false
    },
    authRequest(state, action?: PayloadAction) {
      if (!action?.payload) {
        state.sessionRequested = true
        state.sessionLoading = true
      }
    },
    setLoginLoading(state, action: PayloadAction<boolean>) {
      state.loginLoading = action.payload
    },
    setCsrf(state, action: PayloadAction<{ csrf: string }>) {
      state.csrf = action.payload.csrf
    },
    showRegistrationDialog(state: AuthState) {
      state.registrationDialog = true
    },
    hideRegistrationDialog(state: AuthState) {
      state.registrationDialog = false
    },
    toggleRegistrationDialog(state: AuthState) {
      state.registrationDialog = false
    },
    showRegistrationBanner(state: AuthState) {
      state.registrationBanner = true
    },
    hideRegistrationBanner(state: AuthState) {
      state.registrationBanner = false
    },
  },
})

export const logoutRequest = createAction('auth/logoutRequest')
export const loginRequest = createAction<LoginPayload>('auth/loginRequest')
export const authRequest = createAction<boolean | undefined>('auth/authRequest')

export const {
  authorize,
  unauthorize,
  setLoginLoading,
  setCsrf,
  showRegistrationDialog,
  hideRegistrationDialog,
  toggleRegistrationDialog,
  showRegistrationBanner,
  hideRegistrationBanner,
} = auth.actions

export default auth.reducer
